import { Link, useNavigate } from "@tanstack/react-router"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { ArrowLeft, Loader2, Edit, FileKey, Braces, ListChecks } from "lucide-react"
import { ProfileHeader, ProfileSection } from "@/components/sensnet/ProfileLayout"
import { LiveJsonPreview } from "@/components/rema/shared"

interface PolicyDetailPageProps {
  policyNumber: number | string
}

export function PolicyDetailPage({ policyNumber }: PolicyDetailPageProps) {
  const navigate = useNavigate()

  const { data, isLoading, isError } = useApiQuery("getPolicies", {})
  const policy = ((data as any)?.content?.policies || (data as any)?.policies || []).find(
    (p: any) => String(p.policyNumber) === String(policyNumber)
  )

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <div className="flex items-center gap-2">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
          <span className="text-muted-foreground">Loading policy details...</span>
        </div>
      </div>
    )
  }

  if (isError || !policy) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-4">
        <p className="text-sm text-muted-foreground">Policy not found.</p>
        <Button variant="outline" onClick={() => navigate({ to: '/rema/policies' })}>Back to list</Button>
      </div>
    )
  }

  const policyJson = {
    uri: policy.policyUri,
    version: policy.version,
    role: policy.role,
    allow: policy.allow ?? [],
    deny: policy.deny ?? [],
    resourceInstances: policy.resourceInstances ?? [],
    conditions: policy.conditions ?? {},
  }

  const conditionsEntries = Object.entries(policy.conditions ?? {})

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button asChild variant="ghost" size="sm" className="gap-2 -ml-2">
          <Link to="/rema/policies">
            <ArrowLeft className="h-4 w-4" />
            Back
          </Link>
        </Button>
        <Button asChild size="sm" className="gap-2">
          <Link to="/rema/policies/$policyNumber/edit" params={{ policyNumber: String(policy.policyNumber) }}>
            <Edit className="h-4 w-4" />
            Edit
          </Link>
        </Button>
      </div>

      <ProfileHeader
        title={policy.policyUri}
        subtitle={`Policy Number: ${policy.policyNumber} · v${policy.version}`}
        subtitleIcon={<FileKey className="h-4 w-4" />}
      />

      <ProfileSection title="Policy JSON" icon={<Braces className="h-4 w-4" />} variant="dark">
        <div className="space-y-3 p-5">
          <LiveJsonPreview data={policyJson} />
        </div>
      </ProfileSection>

      <ProfileSection title="Structured Breakdown" icon={<ListChecks className="h-4 w-4" />} variant="dark">
        <div className="space-y-4 p-5">
          <div>
            <p className="text-xs text-muted-foreground mb-1">Role</p>
            <p className="text-sm font-mono">{policy.role || '—'}</p>
          </div>

          {(policy.allow ?? []).length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Allow</p>
              <div className="flex flex-wrap gap-1.5">
                {(policy.allow as string[]).map((perm) => (
                  <Badge key={perm} className="bg-green-100 text-green-800 hover:bg-green-100 text-xs font-mono">
                    {perm}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {(policy.deny ?? []).length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Deny</p>
              <div className="flex flex-wrap gap-1.5">
                {(policy.deny as string[]).map((perm) => (
                  <Badge key={perm} className="bg-red-100 text-red-800 hover:bg-red-100 text-xs font-mono">
                    {perm}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {(policy.resourceInstances ?? []).length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Resource Instances</p>
              <div className="flex flex-wrap gap-1.5">
                {(policy.resourceInstances as string[]).map((uri) => (
                  <Badge key={uri} className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs font-mono">
                    {uri}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {conditionsEntries.length > 0 && (
            <div>
              <p className="text-xs text-muted-foreground mb-1">Conditions</p>
              <div className="rounded-md border overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted/30">
                      <th className="text-left px-3 py-2 font-medium text-xs text-muted-foreground">Key</th>
                      <th className="text-left px-3 py-2 font-medium text-xs text-muted-foreground">Value</th>
                    </tr>
                  </thead>
                  <tbody>
                    {conditionsEntries.map(([key, value]) => (
                      <tr key={key} className="border-t">
                        <td className="px-3 py-2 font-mono text-xs">{key}</td>
                        <td className="px-3 py-2 font-mono text-xs">{String(value)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </ProfileSection>
    </div>
  )
}
